import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { saleService, Sale } from '../services/saleService';
import { clientService, Client } from '../services/clientService';
import { utilityService } from '../services/utilityService';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import {
  People as PeopleIcon,
  AddCircle as AddCircleIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';

interface Activity {
  id: string;
  description: string;
  date: string;
}

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalClients, setTotalClients] = useState(0);
  const [totalSales, setTotalSales] = useState(0);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [averagePrice, setAveragePrice] = useState(0);
  const [recentActivity, setRecentActivity] = useState<Activity[]>([]);

  const { currentUser, userProfile, selectedAgent } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Get today's date in ISO format
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();

      // Fetch all sales and clients in parallel
      if (!userProfile) {
        setError('User profile not available');
        return;
      }
      
      const [allSales, clients] = await Promise.all([
        saleService.getSales(),
        clientService.getClients(userProfile.role, userProfile.role === 'admin' ? (selectedAgent || undefined) : undefined)
      ]);

      // Filter sales for today
      const todaySales = allSales.filter(sale => {
        const saleDate = new Date(sale.date);
        saleDate.setHours(0, 0, 0, 0);
        const saleDateISO = saleDate.toISOString();
        return saleDateISO === todayISO;
      });

      // Calculate totals
      const totalSalesAmount = todaySales.reduce((sum, sale) => sum + (sale.total || 0), 0);
      const totalQuantitySold = todaySales.reduce((sum, sale) => sum + (sale.quantity || 0), 0);
      const avgPrice = totalQuantitySold > 0 ? totalSalesAmount / totalQuantitySold : 0;

      // Create activity list from recent sales and clients
      const activities: Activity[] = [
        ...todaySales
          .filter(sale => sale.id)
          .map(sale => ({
            id: sale.id!,
            description: `Sale recorded for ${sale.clientName}: ${sale.quantity}L at KES ${sale.pricePerLiter.toLocaleString()}/L`,
            date: sale.date
          })),
        ...clients
          .filter(client => client.id)
          .map(client => ({
            id: client.id!,
            description: `New client added: ${client.name}`,
            date: client.date
          }))
      ];

      // Sort activities by date and get the 5 most recent
      const sortedActivities = activities
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 5);

      // Update state with calculated values
      setTotalClients(clients.length);
      setTotalSales(totalSalesAmount);
      setTotalQuantity(totalQuantitySold);
      setAveragePrice(avgPrice);
      setRecentActivity(sortedActivities);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Dashboard
      </Typography>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '50vh'
        }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Today's Summary
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Clients
                    </Typography>
                    <Typography variant="h5">
                      {totalClients}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Sales
                    </Typography>
                    <Typography variant="h5">
                      KES {totalSales.toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Quantity
                    </Typography>
                    <Typography variant="h5">
                      {totalQuantity.toFixed(2)} L
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Average Price
                    </Typography>
                    <Typography variant="h5">
                      KES {averagePrice.toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            {recentActivity.length > 0 ? (
              <List>
                {recentActivity.map((activity, index) => (
                  <React.Fragment key={activity.id}>
                    <ListItem>
                      <ListItemText
                        primary={activity.description}
                        secondary={utilityService.formatDate(activity.date)}
                      />
                    </ListItem>
                    {index < recentActivity.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography color="textSecondary">No recent activity</Typography>
            )}
          </Paper>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
                onClick={() => navigate('/clients')}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6">Add Client</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
                onClick={() => navigate('/sales')}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <AddCircleIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6">Record Sale</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
                onClick={() => navigate('/reports')}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <BarChartIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6">Generate Report</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default Dashboard; 